import React, { useState, useRef } from 'react';
import { Typography, Box, Card, CardContent, IconButton, Button } from '@mui/material';
import { ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon, Reply as ReplyIcon, Edit as EditIcon, Delete as DeleteIcon, Report as ReportIcon, Gavel as GavelIcon } from '@mui/icons-material';
import { CommunityBoardDAO } from '../../../../repository/CommunityBoardDAO';
import { useUserContext } from '../../../../context/user_context/UserContext';
import { useToast } from '../../../../context/toast/ToastContext';
import { formatDistanceToNow } from 'date-fns';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Timestamp } from 'firebase/firestore';
import DOMPurify from 'dompurify';
import AddReplyModal from './AddReplyModal';
import ConfirmDeleteModal from '../../../../components/modals/ConfirmDeleteModal';

//TODO:String Resources Translations

const getIndentationColor = (depth) => {
  const colors = ['#818384', '#36393f', '#666769', '#2c2d2f'];
  return colors[depth % colors.length];
};

const Comment = ({ 
  comment, 
  postId, 
  threadId, 
  depth = 0, 
  setMessages,
  userVotes,
  onVoteChange,
  onReport,
  onModerate,
  isAdmin,
}) => {
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { firebaseUser } = useUserContext();
  const { showToast } = useToast();
  const [loadingMoreReplies, setLoadingMoreReplies] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleReply = async (replyContent) => {
    try {
      const newReplyId = await CommunityBoardDAO.addMessage(
        postId,
        {
          message: replyContent,
          creator: firebaseUser 
            ? `${firebaseUser.first_name || ''} ${firebaseUser.last_name || ''}`.trim() 
            : 'Anonymous',
          creator_id: firebaseUser ? firebaseUser.uid : null,
        },
        comment.id
      );

      const newReply = {
        id: newReplyId,
        message: replyContent,
        creator: firebaseUser 
          ? `${firebaseUser.first_name || ''} ${firebaseUser.last_name || ''}`.trim() 
          : 'Anonymous',
        creator_id: firebaseUser ? firebaseUser.uid : null,
        created_at: Timestamp.now(),
        vote_score: 1,
        parent_id: comment.id,
        replies: []
      };

      // Helper function to insert reply recursively
      const insertReplyRecursively = (comments) => {
        for (let i = 0; i < comments.length; i++) {
          if (comments[i].id === comment.id) {
            if (!comments[i].replies) comments[i].replies = [];
            comments[i].replies.push(newReply);
            return true;
          }

          if (comments[i].replies && comments[i].replies.length > 0) {
            if (insertReplyRecursively(comments[i].replies)) return true;
          }
        }
        return false;
      };

      // Update messages state
      setMessages(prevMessages => {
        const updatedMessages = [...prevMessages];
        insertReplyRecursively(updatedMessages);
        return updatedMessages;
      });

      showToast('Reply added successfully', 'success');
    } catch (error) {
      showToast('Failed to add reply', 'error');
      console.error('Reply error:', error);
    }
  };

  const handleVote = async (isUpvote) => {
    if (!firebaseUser) {
      showToast('You must be logged in to vote', 'error');
      return;
    }

    let voteChange = 0;
    let newVoteState = null;
    try {
      const existingVote = userVotes?.[comment.id];
      
      // If clicking the same vote type (e.g. upvote → remove upvote)
      if (existingVote === isUpvote) {
        voteChange = isUpvote ? -1 : 1; // Remove upvote (-1) or remove downvote (+1)
        newVoteState = null; // Remove the vote
      }
      // If no previous vote (null) or changing vote type
      else {
        if (existingVote === null || existingVote === undefined) {
          // First vote: upvote (+1) or downvote (-1)
          voteChange = isUpvote ? 1 : -1;
          newVoteState = isUpvote;
        } else {
          // Changing from upvote to downvote (-2) or downvote to upvote (+2)
          voteChange = isUpvote ? 2 : -2;
          newVoteState = isUpvote;
        }
      }

      // Update messages state optimistically
      setMessages(prevMessages => {
        const updateCommentRecursively = (comments) => {
          return comments.map(c => {
            if (c.id === comment.id) {
              return {
                ...c,
                vote_score: (c.vote_score || 0) + voteChange
              };
            }
            if (c.replies && c.replies.length > 0) {
              return { ...c, replies: updateCommentRecursively(c.replies) };
            }
            return c;
          });
        };
        return updateCommentRecursively(prevMessages);
      });

      // Make the API call
      await CommunityBoardDAO.voteOnMessage(
        postId, 
        comment.id, 
        firebaseUser.uid, 
        isUpvote,
        comment.parent_id
      );

      // Update userVotes state optimistically
      const updatedVotes = { ...userVotes, [comment.id]: newVoteState };
      onVoteChange?.(updatedVotes);

    } catch (error) {
      // Revert optimistic update on error
      setMessages(prevMessages => {
        const updateCommentRecursively = (comments) => {
          return comments.map(c => {
            if (c.id === comment.id) {
              return {
                ...c,
                vote_score: (c.vote_score || 0) - voteChange
              };
            }
            if (c.replies && c.replies.length > 0) {
              return { ...c, replies: updateCommentRecursively(c.replies) };
            }
            return c;
          });
        };
        return updateCommentRecursively(prevMessages);
      });

      // Revert userVotes state on error
      const revertedVotes = { ...userVotes };
      onVoteChange?.(revertedVotes);

      console.error('Vote error:', error);
      showToast('Failed to vote', 'error');
    }
  };

  const toggleCollapse = (e) => {
    e.stopPropagation();
    setIsCollapsed(!isCollapsed);
  };

  const getReplyCount = (comment) => {
    const replies = comment.replies?.replies || comment.replies || [];
    let count = replies.length || 0;
    replies.forEach(reply => {
      count += getReplyCount(reply);
    });
    return count;
  };

  const handleLoadMoreReplies = async () => {
    if (loadingMoreReplies) return;
    
    setLoadingMoreReplies(true);
    try {
      const lastReply = comment.replies[comment.replies.length - 1];
      const moreReplies = await CommunityBoardDAO.loadMoreReplies(postId, comment.id, lastReply.id, { limit: 3 });
      setMessages(prevMessages => {
        const updateRepliesRecursively = (comments) => {
          return comments.map(c => {
            if (c.id === comment.id) return { ...c, replies: [...c.replies, ...moreReplies] };
            if (c.replies && c.replies.length > 0) return {...c, replies: updateRepliesRecursively(c.replies)};
            return c;
          });
        };
        return updateRepliesRecursively(prevMessages);
      });
    } catch (error) {
      showToast('Failed to load more replies', 'error');
    } finally {
      setLoadingMoreReplies(false);
    }
  };

  const handleDeleteComment = async (e) => {
    e.stopPropagation();
    if (!firebaseUser || firebaseUser.uid !== comment.creator_id) {
      showToast('You can only delete your own comments', 'error');
      return;
    }
    console.log(comment, threadId, postId);
    setShowDeleteModal(true);
  };

  const confirmDeleteComment = async () => {
    try {
      await CommunityBoardDAO.moderateContent(comment.id, 'comment', 'deleted', firebaseUser.uid);
      
      // Update the messages state to mark this comment as deleted
      setMessages(prevMessages => {
        const updateCommentRecursively = (comments) => {
          return comments.map(c => {
            if (c.id === comment.id) {
              return {
                ...c,
                deleted: true,
                deleted_at: Timestamp.now(),
                deleted_by: firebaseUser.uid,
                moderation_action: 'deleted'
              };
            }
            if (c.replies && c.replies.length > 0) {
              return { ...c, replies: updateCommentRecursively(c.replies) };
            }
            return c;
          });
        };
        return updateCommentRecursively(prevMessages);
      });

      showToast('Comment deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting comment:', error);
      showToast('Failed to delete comment', 'error');
    } finally {
      setShowDeleteModal(false);
    }
  };

  return (
    <Box 
      sx={{ 
        ml: { 
          xs: Math.min(depth, 4) * 1,
          sm: Math.min(depth, 6) * 2
        },
        mb: 0.5,
        position: 'relative',
        borderLeft: depth > 0 ? '2px solid' : 'none',
        borderColor: depth > 0 ? getIndentationColor(depth) : 'transparent',
        pl: depth > 0 ? { xs: 1, sm: 1.5 } : 0,
      }}
    >
      <Card 
        variant="outlined"
        sx={{ 
          cursor: comment.replies?.length > 0 ? 'pointer' : 'default',
          '&:hover': comment.replies?.length > 0 ? {
            backgroundColor: 'action.hover'
          } : {},
          boxShadow: 'none',
          border: 'none',
          bgcolor: 'transparent',
        }}
        onClick={comment.replies?.length > 0 ? toggleCollapse : undefined}
      >
        <CardContent 
          sx={{ 
            p: { xs: 0.5, sm: 1 }, // Reduce padding further
            '&:last-child': { pb: 0.5 },
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            {/* Vote buttons - even more compact */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              minWidth: { xs: 24, sm: 32 }
            }}>
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleVote(true);
                }}
                sx={{ 
                  p: { xs: 0.25, sm: 0.5 },
                  '&:hover': { bgcolor: 'transparent' }
                }}
                color={userVotes?.[comment.id] === true ? 'primary' : 'default'}
              >
                <ArrowUpIcon 
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                />
              </IconButton>
              <Typography 
                variant="caption"
                sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 500,
                  lineHeight: 1
                }}
              >
                {comment.vote_score || 0}
              </Typography>
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleVote(false);
                }}
                sx={{ 
                  p: { xs: 0.25, sm: 0.5 },
                  '&:hover': { bgcolor: 'transparent' }
                }}
                color={userVotes?.[comment.id] === false ? 'error' : 'default'}
              >
                <ArrowDownIcon 
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                />
              </IconButton>
            </Box>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              {/* Comment metadata */}
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  display: 'block',
                  mb: 0.5,
                  fontSize: { xs: '0.625rem', sm: '0.75rem' }
                }}
              >
                {comment.creator} • {formatDistanceToNow(comment.created_at.toDate())} ago
              </Typography>

              {/* Comment content */}
              <Typography 
                variant="body2"
                component="div"
                sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  '& p': { 
                    m: 0,
                    mb: 1,
                    '&:last-child': {
                      mb: 0
                    }
                  }
                }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(comment.message)
                }}
              />

              {/* Action buttons */}
              <Box 
                sx={{ 
                  mt: 1, 
                  display: 'flex', 
                  gap: 1,
                  alignItems: 'center'
                }}
              >
                {!comment.deleted && (
                  <>
                    <Button
                      startIcon={<ReplyIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowReplyModal(true);
                      }}
                      sx={{ 
                        fontSize: { xs: '0.625rem', sm: '0.75rem' },
                        textTransform: 'none',
                        color: 'text.secondary',
                        p: { xs: '2px 4px', sm: '4px 8px' },
                        minWidth: { xs: 'auto', sm: 0 }
                      }}
                    >
                      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Reply</Box>
                    </Button>

                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        onReport(comment.id);
                      }}
                      startIcon={<ReportIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} />}
                      sx={{ 
                        fontSize: { xs: '0.625rem', sm: '0.75rem' },
                        textTransform: 'none',
                        color: 'text.secondary',
                        p: { xs: '2px 4px', sm: '4px 8px' },
                        minWidth: { xs: 'auto', sm: 0 }
                      }}
                    >
                      <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Report</Box>
                    </Button>

                    {isAdmin && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          onModerate(comment.id, e);
                        }}
                        startIcon={<GavelIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} />}
                        sx={{ 
                          fontSize: { xs: '0.625rem', sm: '0.75rem' },
                          textTransform: 'none',
                          color: 'green',
                          p: { xs: '2px 4px', sm: '4px 8px' },
                          minWidth: { xs: 'auto', sm: 0 }
                        }}
                      >
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Mod Tools</Box>
                      </Button>
                    )}

                    {firebaseUser?.uid === comment.creator_id && (
                      <Button
                        onClick={handleDeleteComment}
                        startIcon={<DeleteIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }} />}
                        sx={{ 
                          fontSize: { xs: '0.625rem', sm: '0.75rem' },
                          textTransform: 'none',
                          color: 'error.main',
                          p: { xs: '2px 4px', sm: '4px 8px' },
                          minWidth: { xs: 'auto', sm: 0 }
                        }}
                      >
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Delete</Box>
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Nested replies */}
      {!isCollapsed && comment.replies && comment.replies.length > 0 && (
        <Box>
          {comment.replies.map((reply) => (
            <Comment
              key={reply.id}
              comment={reply}
              postId={postId}
              threadId={threadId}
              depth={depth + 1}
              setMessages={setMessages}
              userVotes={userVotes}
              onVoteChange={onVoteChange}
              onReport={onReport}
              onModerate={onModerate}
              isAdmin={isAdmin}
            />
          ))}
          {comment.hasMoreReplies && (
            <Button
              onClick={handleLoadMoreReplies}
              disabled={loadingMoreReplies}
              sx={{ 
                ml: { xs: 4, sm: 6 },
                mt: 1,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                p: { xs: '2px 4px', sm: '4px 8px' }
              }}
              size="small"
            >
              {loadingMoreReplies ? 'Loading...' : 'Load More Replies'}
            </Button>
          )}
        </Box>
      )}

      {/* Add Reply Modal */}
      <AddReplyModal
        show={showReplyModal}
        onHide={() => setShowReplyModal(false)}
        onSubmit={handleReply}
      />
      
      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteComment}
        item="this comment"
      />
    </Box>
  );
};

export default Comment; 